//* General
import { Component, inject, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterModule, RouterOutlet } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Location, NgClass, NgOptimizedImage, ViewportScroller } from '@angular/common';
import { version } from 'package.json';

//* RXJS
import { filter, Subject, takeUntil } from 'rxjs';

//* Material
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';

//* Components
import { HeaderComponent } from '@components/header/header.component';

//* Models
import { ISyspolMenu } from '@models/modules.model';

//* Services
import { IconName, IconService } from '@services/icon.service';
import { SessionService } from '@services/session';
import { MenuService } from '@services/menu';

//* Shared
import { MODULES_URLS } from '@shared/data/data';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, TranslateModule, RouterModule, MatIconModule, MatSidenavModule, HeaderComponent, NgClass, NgOptimizedImage],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {

  /* SERVICES */
  readonly #iconService = inject(IconService);
  readonly #sessionService = inject(SessionService);
  readonly #menuService = inject(MenuService);
  /* SERVICES */

  /* UTILS */
  readonly #viewportScroller = inject(ViewportScroller);
  readonly #router = inject(Router);
  readonly #location = inject(Location);
  /* UTILS */

  //* Array de subdominios válidos
  private subs = new Subject<void>();
  private allModuleUrls: { url: string; idMenu: number; title: string; }[] = MODULES_URLS;

  public showNav = false;
  public auth = true;
  public idMenu = 0;
  public version = version;
  public menuWithPermissions: ISyspolMenu[] = [];

  ngOnInit(): void {
    this.loadIcons();

    this.#router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.subs)
      ).subscribe(async () => {
        this.#viewportScroller.scrollToPosition([0, 0]);
        this.auth = this.#location.path().includes('auth');
        for (const module of this.allModuleUrls) {
          this.#location.path().includes(module.url) ? this.idMenu = module.idMenu : null;
        }
        this.menuWithPermissions = await this.#menuService.setModulesPermissions(this.idMenu);
      });
  }

  private loadIcons(): void {
    const essentialIcons: IconName[] = ['ic-user', 'ic-events-kpi', 'ic-personal', 'ic-municipal-police', 'ic-prosecution', 'ic-state-police', 'ic-logout', 'ic-sidebar', 'ic-down-blue', 'ic-police', 'ic-patrulla', 'ic-employees', 'ic-inventory', 'ic-plazas', 'ic-listas', 'ic-asistencia', 'ic-ARMERO', 'ic-courses', 'ic-arrested-pending', 'ic-jail', 'ic-statistics', 'ic-medical-certification', 'ic-tamizaje', 'ic-incidents', 'ic-logo-p', 'ic-trash', 'ic-eye', 'ic-download', 'ic-close', 'ic-edit', 'ic-iph', 'ic-edit-white', 'ic-trash-white', 'ic-visibility', 'ic-repuve', 'ic-attachment', 'ic-download-blue', 'ic-alert-blue', 'ic-alert-red', 'ic-arrow-down-white', 'ic-arrow-up-white', 'ic-orden-hearing', 'ic-audiencia', 'ic-events', 'ic-contact', 'ic-check-weapons', 'ic-search', 'ic-foreigners', 'ic-nationals', 'ic-assignar', 'ic-reportes', 'ic-patrullaje', 'ic-llamadaAuxilio'];
    this.#iconService.loadIcons(essentialIcons);
  }

  /**
  * @method showNavigation
  * @description Muestra u oculta la barra de navegación lateral.
  */
  public showNavigation(): void {
    this.showNav = !this.showNav;
  }

  /**
  * @method goRouter
  * @description Navega a la URL proporcionada y, opcionalmente, actualiza el estado del menú según el ID proporcionado.
  * @param url La URL a la que navegar.
  * @param idMenu (Opcional) El ID del menú para actualizar su estado de apertura.
  */
  public goRouter(url: string, idMenu?: number): void {
    //* Actualiza el estado del menú si se proporciona un ID válido.
    if (idMenu !== undefined && idMenu !== null) {
      this.idMenu = idMenu;
      this.menuWithPermissions.forEach((menu: any) => {
        menu.open = menu.idMenu.includes(idMenu);
      });
    }
    //* Oculta la navegación y realiza la redirección.
    this.showNav = false;

    const dUrl = url.split('/');
    this.#router.navigate(dUrl);
  }

  /**
  * @method openMenu
  * @description Abre o cierra un menú en particular en la barra de navegación.
  * @param index El índice del menú en el arreglo para abrir o cerrar.
  */
  public openMenu(index: number): void {
    this.menuWithPermissions[index].open = !this.menuWithPermissions[index].open;
  }

  /**
  * @method signOff
  * @description Cierra la sesión actual, eliminando la información de rol, módulos y menú.
  * @description También redirige a la página de autenticación.
  */
  public signOff(): void {
    this.idMenu = 0;
    this.menuWithPermissions = [];
    this.#sessionService.endSession();
  }
}
